export interface ToggleGroupOption<T extends string> {
    value: T;
    label: string;
    disabled?: boolean;
    /** Id of visible text explaining why the option is disabled. */
    describedBy?: string;
}

export interface ToggleGroupProps<T extends string> {
    options: ToggleGroupOption<T>[];
    value: T;
    onChange: (value: T) => void;
    ariaLabel: string;
    className?: string;
}

const OPTION_CLASSES =
    'rounded-md px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mp-primary';
const SELECTED_CLASSES = 'bg-mp-surface text-mp-foreground shadow';
const UNSELECTED_CLASSES = 'text-mp-foreground/70 hover:text-mp-foreground';

export function ToggleGroup<T extends string>({ options, value, onChange, ariaLabel, className = '' }: ToggleGroupProps<T>) {
    return (
        <div role="group" aria-label={ariaLabel} className={`inline-flex rounded-lg bg-mp-border p-0.5 ${className}`.trim()}>
            {options.map((option) => {
                const isSelected = option.value === value;
                const isDisabled = Boolean(option.disabled) && !isSelected;
                return (
                    <button
                        key={option.value}
                        type="button"
                        aria-pressed={isSelected}
                        aria-disabled={isDisabled || undefined}
                        aria-describedby={isDisabled ? option.describedBy : undefined}
                        className={`${OPTION_CLASSES} ${isSelected ? SELECTED_CLASSES : UNSELECTED_CLASSES}${isDisabled ? ' cursor-not-allowed opacity-50' : ''}`}
                        onClick={() => {
                            if (!isSelected && !isDisabled) onChange(option.value);
                        }}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
